import { FIGHTER, isSlowed, type MatchState, type SimEvent } from "../sim";
import { cuesFor } from "./cues";
import { SOUNDS } from "./library";
import { noiseBuffer, playRecipe } from "./synth";
import { wavBytes } from "./wav";

/**
 * Звук боя (консультант 05.11.2026: «удары по силе, блок, шаги, нокаут, глухой звук и сердце в замедлении»): события
 * мира → звуки (audio/cues.ts), шаги — по ходьбе и бегу, в замедлении решающего удара всё глохнет (низкие частоты) и
 * бьётся сердце. Только слушает мир, ничего в нём не меняет. В съёмке не играет, а пишет звуки по шагам мира — потом
 * дорожка собирается офлайн теми же рецептами (render) и ложится на ролик.
 */
const VOLUME = 0.7;
/** Лимитер на выходе: потолок около −1 dB (проба давала пик 1,17 при шкале 1,0). */
const LIMIT_THRESHOLD = -8;
const LIMIT_CEILING = 0.89;
/** Атмосфера арены выключена: синтезированный шум Джон слышит как сильный шум на фоне (05.11.2026). */
const AMBIENCE_GAIN = 0;
/** «Воздух» зала — середина, слышна и на динамиках телефона и ТВ. */
const AIR_GAIN = 0;
/** Глухо в замедлении: срез верхов, Гц; обычный звук — почти без среза. */
const MUFFLED = 2000;
const OPEN = 18000;
/** Резонанс среза в замедлении — «закрывается» заметно, а не просто тише. */
const MUFFLE_Q = 1.2;
/** Замедление растягивает звуки: длиннее и ниже во столько раз. */
const SLOW_STRETCH = 1.5;
/** Сердце в замедлении — раз в столько шагов мира (~0,8 с). */
const HEARTBEAT_EVERY = 48;
/** Шаг — раз в столько кадров ходьбы и бега. */
const WALK_STEP = 15;
const RUN_STEP = 9;
const WORLD_STEP = 1 / 60;

interface RecordedCue {
  readonly step: number;
  readonly name: string;
  readonly gain: number;
  readonly muffled: boolean;
}

export class AudioSystem {
  private context: AudioContext | null = null;
  private muffle: BiquadFilterNode | null = null;
  private noise: AudioBuffer | null = null;
  private step = 0;
  private heartbeatIn = 0;
  private readonly recorded: RecordedCue[] = [];
  private recordFrom = 0;

  /** record — съёмка: звуки пишутся, а не играют. */
  constructor(private readonly record = false) {}

  /** Включить звук по жесту игрока (браузер не даёт звучать без клика или клавиши). */
  unlock(): void {
    if (this.record) return;
    if (!this.context) {
      const context = new AudioContext();
      const muffle = context.createBiquadFilter();
      muffle.type = "lowpass";
      muffle.frequency.value = OPEN;
      muffle.Q.value = MUFFLE_Q;
      const master = context.createGain();
      master.gain.value = VOLUME;
      muffle.connect(master);
      master.connect(limiter(context));
      this.context = context;
      this.muffle = muffle;
      this.noise = noiseBuffer(context);
      ambience(context, master, this.noise, 0, Infinity);
    }
    if (this.context.state === "suspended") void this.context.resume();
  }

  /** Шаг мира: звуки событий, шаги, глухо и сердце в замедлении. */
  onStep(events: readonly SimEvent[], state: MatchState): void {
    this.step++;
    const slowed = isSlowed(state);
    for (const event of events) for (const cue of cuesFor(event)) this.play(cue.name, cue.gain, slowed);
    for (const fighter of state.fighters) {
      const walking = fighter.state === FIGHTER.walkForward || fighter.state === FIGHTER.walkBack;
      const every = fighter.running > 0 ? RUN_STEP : WALK_STEP;
      if (walking && fighter.stateFrame % every === every - 1)
        this.play("step", fighter.running > 0 ? 0.7 : 0.5, slowed);
    }
    if (slowed) {
      if (this.heartbeatIn <= 0) {
        this.play("heartbeat", 1, false);
        this.heartbeatIn = HEARTBEAT_EVERY;
      }
      this.heartbeatIn--;
    } else this.heartbeatIn = 0;
    if (this.muffle && this.context)
      this.muffle.frequency.setTargetAtTime(slowed ? MUFFLED : OPEN, this.context.currentTime, 0.08);
  }

  private play(name: string, gain: number, muffled: boolean): void {
    if (this.record) {
      this.recorded.push({ step: this.step, name, gain, muffled });
      return;
    }
    const recipe = SOUNDS[name];
    if (!recipe || !this.context || !this.muffle || !this.noise || this.context.state !== "running") return;
    playRecipe(
      this.context,
      this.muffle,
      recipe,
      this.context.currentTime,
      gain,
      this.noise,
      muffled ? SLOW_STRETCH : 1,
    );
  }

  /** Съёмка: начать дорожку с этого шага мира (после пропуска). */
  markStart(): void {
    this.recordFrom = this.step;
  }

  /** Съёмка: какие звуки прозвучали от отметки — секунда, имя, громкость, глухо ли (список для прослушивания). */
  cueList(): { at: number; name: string; gain: number; muffled: boolean }[] {
    return this.recorded
      .filter((cue) => cue.step >= this.recordFrom)
      .map((cue) => ({
        at: Math.round((cue.step - this.recordFrom) * WORLD_STEP * 100) / 100,
        name: cue.name,
        gain: cue.gain,
        muffled: cue.muffled,
      }));
  }

  /** Съёмка: дорожка WAV от отметки до нынешнего шага — те же рецепты, офлайн. */
  async renderTrack(): Promise<Uint8Array> {
    const steps = Math.max(1, this.step - this.recordFrom);
    const rate = 48000;
    const context = new OfflineAudioContext(2, Math.ceil(steps * WORLD_STEP * rate), rate);
    const noise = noiseBuffer(context);
    const master = context.createGain();
    master.gain.value = VOLUME;
    master.connect(limiter(context));
    const muffle = context.createBiquadFilter();
    muffle.type = "lowpass";
    muffle.frequency.value = MUFFLED;
    muffle.Q.value = MUFFLE_Q;
    muffle.connect(master);
    ambience(context, master, noise, 0, steps * WORLD_STEP);
    for (const cue of this.recorded) {
      if (cue.step < this.recordFrom) continue;
      const recipe = SOUNDS[cue.name];
      if (!recipe) continue;
      playRecipe(
        context,
        cue.muffled ? muffle : master,
        recipe,
        (cue.step - this.recordFrom) * WORLD_STEP,
        cue.gain,
        noise,
      );
    }
    return wavBytes(await context.startRendering());
  }
}

/** Лимитер на выходе: сжатие выше порога и потолок — звук не перегружается. */
function limiter(context: BaseAudioContext): AudioNode {
  const compressor = context.createDynamicsCompressor();
  compressor.threshold.value = LIMIT_THRESHOLD;
  compressor.knee.value = 0;
  compressor.ratio.value = 20;
  compressor.attack.value = 0.002;
  compressor.release.value = 0.12;
  const ceiling = context.createGain();
  ceiling.gain.value = LIMIT_CEILING;
  compressor.connect(ceiling);
  ceiling.connect(context.destination);
  return compressor;
}

/** Атмосфера арены: тихий низкий гул зала (шум с низким срезом) под всем боем — от start до end (Infinity — всегда). */
function ambience(
  context: BaseAudioContext,
  destination: AudioNode,
  noise: AudioBuffer,
  start: number,
  end: number,
): void {
  // гул зала (низ) и «воздух» (середина) — два слоя одного шума
  const layers: { type: BiquadFilterType; frequency: number; q: number; gain: number }[] = [
    { type: "bandpass", frequency: 220, q: 0.6, gain: AMBIENCE_GAIN },
    { type: "bandpass", frequency: 1200, q: 0.7, gain: AIR_GAIN },
  ];
  for (const layer of layers) {
    const source = context.createBufferSource();
    source.buffer = noise;
    source.loop = true;
    source.loopStart = 0;
    const filter = context.createBiquadFilter();
    filter.type = layer.type;
    filter.frequency.value = layer.frequency;
    filter.Q.value = layer.q;
    const gain = context.createGain();
    gain.gain.value = layer.gain;
    source.connect(filter);
    filter.connect(gain);
    gain.connect(destination);
    source.start(start, layer.frequency < 500 ? 0 : 0.5);
    if (Number.isFinite(end)) source.stop(end);
  }
}
